import React, { useCallback, useEffect, useMemo, useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import {
  getMyPreferences,
  putMyPreferences,
  UserPreference,
} from "../requests/request";
import { RESOURCE_TYPE_LABELS } from "../types";

const RESOURCE_DESCRIPTIONS: Record<string, string> = {
  port: "Get notified for any event on any Port in this tenant.",
  "cloud-connectivity":
    "Get notified for any event on any Cloud Connectivity service.",
  "datacenter-interconnect":
    "Get notified for any event on any Data Center Interconnect.",
  "sdwan-hub": "Get notified for any event on any SD-WAN Hub.",
  fwaas: "Get notified for any event on any Virtual Firewall (FWaaS).",
  backbone: "Get notified for any event on any Backbone service.",
  internet: "Get notified for any event on any Internet service.",
  "bgp-session": "Get notified for any event on any BGP session.",
  reflector: "Get notified for any event on any Reflector v2.0 instance.",
  // CTX-19 — subscription expiry milestones, not a per-event resource.
  subscription:
    "Get notified before a service subscription expires: 90, 60, 30, 7, 3 and 1 day before, and on the expiration date.",
};

const MyNotificationsTab: React.FC = () => {
  const api = useAxiosAuth0();
  const tenantId = useAppSelector((s) => s.userProfile.tenantId);

  const [preferences, setPreferences] = useState<UserPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingTypes, setPendingTypes] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<{ targetEnabled: boolean } | null>(null);
  const [bulkPending, setBulkPending] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getMyPreferences(api, tenantId);
      setPreferences(res.data.data);
    } catch {
      setError("Couldn't load your preferences");
    } finally {
      setLoading(false);
    }
  }, [api, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const enabledCount = useMemo(
    () => preferences.filter((p) => p.enabled).length,
    [preferences]
  );

  // Smart bulk action: when everything is on, the button unsubscribes;
  // otherwise (off or mixed) it subscribes — closing the loop towards "all on".
  const allOn = preferences.length > 0 && enabledCount === preferences.length;
  const bulkTarget = !allOn;
  const bulkActionLabel = bulkTarget
    ? "Subscribe to all"
    : "Unsubscribe from all";
  const bulkDisabled =
    loading ||
    bulkPending ||
    pendingTypes.size > 0 ||
    preferences.length === 0;

  const handleBulkConfirm = async () => {
    if (!tenantId || !bulkConfirm) return;
    const target = bulkConfirm.targetEnabled;
    const previousState = preferences;
    const allTypes = preferences.map((p) => p.resourceType);

    setBulkPending(true);
    setPendingTypes(new Set(allTypes));
    setPreferences((prev) => prev.map((p) => ({ ...p, enabled: target })));

    try {
      await putMyPreferences(
        api,
        tenantId,
        allTypes.map((resourceType) => ({ resourceType, enabled: target }))
      );
      setError(null);
    } catch {
      setPreferences(previousState);
      setError(
        target
          ? "Couldn't subscribe to all resource types"
          : "Couldn't unsubscribe from all resource types"
      );
    } finally {
      setBulkPending(false);
      setPendingTypes(new Set());
      setBulkConfirm(null);
    }
  };

  const handleToggle = async (resourceType: string, nextEnabled: boolean) => {
    if (!tenantId) return;
    // Optimistic update — flip the row immediately, persist in background.
    setPreferences((prev) =>
      prev.map((p) =>
        p.resourceType === resourceType ? { ...p, enabled: nextEnabled } : p
      )
    );
    setPendingTypes((prev) => {
      const next = new Set(prev);
      next.add(resourceType);
      return next;
    });
    try {
      // The PUT response only returns rows that exist in the DB
      // (toggle-ON ones), so replacing local state with it would drop
      // every OFF row. The optimistic update above is already the
      // correct full state; just confirm it persisted.
      await putMyPreferences(api, tenantId, [
        { resourceType, enabled: nextEnabled },
      ]);
      setError(null);
    } catch {
      // Rollback on failure
      setPreferences((prev) =>
        prev.map((p) =>
          p.resourceType === resourceType
            ? { ...p, enabled: !nextEnabled }
            : p
        )
      );
      setError(`Couldn't save preference for ${RESOURCE_TYPE_LABELS[resourceType] ?? resourceType}`);
    } finally {
      setPendingTypes((prev) => {
        const next = new Set(prev);
        next.delete(resourceType);
        return next;
      });
    }
  };

  return (
    <div className="space-y-6">
      <GenericCard isCollapsible={false} className="flex flex-col overflow-hidden">
        <div className="flex flex-col">
            <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4">
              <div>
                <h2 className="text-base font-semibold text-semantic-text-primary">
                  My notification preferences
                </h2>
                <p className="text-sm text-semantic-text-secondary mt-1 max-w-2xl">
                  Toggle which resource types you want to be notified about.
                  These preferences apply to{" "}
                  <strong>all current and future resources</strong> of that type
                  in your active tenant. They&apos;re separate from Notification
                  Groups — you receive an alert if either source covers the
                  event.
                </p>
              </div>
              <GenericButton
                label={bulkActionLabel}
                variant="secondary"
                size="sm"
                disabled={bulkDisabled}
                loading={bulkPending}
                onClick={() => setBulkConfirm({ targetEnabled: bulkTarget })}
              />
            </div>

            {error && (
              <div className="px-5 pb-4">
                <div className="px-4 py-3 rounded-md text-sm bg-red-50 text-red-700 border border-red-200 flex items-center justify-between gap-3">
                  <span>{error}</span>
                  <GenericButton
                    label="Retry"
                    variant="secondary"
                    size="sm"
                    onClick={load}
                  />
                </div>
              </div>
            )}

            <div className="overflow-hidden border-t border-semantic-border-secondary">
              <div className="grid grid-cols-[1fr_auto] gap-4 px-5 py-3 border-b border-semantic-border-secondary bg-semantic-bg-secondary">
                <div className="text-xs uppercase tracking-wide text-semantic-text-secondary">
                  Resource type
                </div>
                <div className="text-xs uppercase tracking-wide text-semantic-text-secondary text-right">
                  Notify me
                </div>
              </div>

              {loading && preferences.length === 0 ? (
                <div className="divide-y divide-semantic-border-secondary">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1fr_auto] gap-4 px-5 py-4 items-center"
                    >
                      <div className="space-y-1.5">
                        <span
                          className="block h-4 w-44 rounded bg-semantic-bg-secondary animate-pulse"
                          aria-hidden
                        />
                        <span
                          className="block h-3 w-72 rounded bg-semantic-bg-secondary animate-pulse"
                          aria-hidden
                        />
                      </div>
                      <span
                        className="h-6 w-11 rounded-full bg-semantic-bg-secondary animate-pulse"
                        aria-hidden
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-semantic-border-secondary">
                  {preferences.map((p) => {
                    const label =
                      RESOURCE_TYPE_LABELS[p.resourceType] ?? p.resourceType;
                    const description =
                      RESOURCE_DESCRIPTIONS[p.resourceType] ??
                      `Get notified for any event on any ${label}.`;
                    const isPending = pendingTypes.has(p.resourceType);
                    return (
                      <div
                        key={p.resourceType}
                        className={`grid grid-cols-[1fr_auto] gap-4 px-5 py-4 items-center transition-opacity ${
                          isPending ? "opacity-60" : ""
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-semantic-text-primary">
                            {label}
                          </div>
                          <p className="text-xs text-semantic-text-secondary mt-0.5">
                            {description}
                          </p>
                        </div>
                        <GenericSwitch
                          checked={p.enabled}
                          onChange={(next) =>
                            handleToggle(p.resourceType, next)
                          }
                          disabled={isPending}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!loading && preferences.length > 0 && (
              <div className="px-5 py-4 border-t border-semantic-border-secondary bg-semantic-bg-primary">
                <p className="text-xs text-semantic-text-secondary">
                  {enabledCount === 0
                    ? "You won't receive type-level personal notifications. You may still get events through Notification Groups you're a member of."
                    : enabledCount === preferences.length
                    ? "You're subscribed to every resource type. Toggle individually to scope down."
                    : `Subscribed to ${enabledCount} of ${preferences.length} resource types.`}
                </p>
              </div>
            )}
          </div>
      </GenericCard>

      <GenericModal
        isOpen={bulkConfirm !== null}
        onClose={() => {
          if (!bulkPending) setBulkConfirm(null);
        }}
        size="sm"
        title={
          bulkConfirm?.targetEnabled
            ? "Subscribe to all resource types?"
            : "Unsubscribe from all resource types?"
        }
        footer={
          <>
            <GenericButton
              label="Cancel"
              variant="secondary"
              size="sm"
              disabled={bulkPending}
              onClick={() => setBulkConfirm(null)}
            />
            <GenericButton
              label={
                bulkConfirm?.targetEnabled
                  ? "Subscribe to all"
                  : "Unsubscribe from all"
              }
              variant={bulkConfirm?.targetEnabled ? "brand" : "danger"}
              size="sm"
              loading={bulkPending}
              onClick={handleBulkConfirm}
            />
          </>
        }
      >
        <p className="text-sm text-semantic-text-primary">
          {bulkConfirm?.targetEnabled ? (
            <>
              You&apos;ll start receiving personal alerts for events on every
              resource type in this tenant — including{" "}
              <strong>resources created in the future</strong>. You can adjust
              each type individually anytime.
            </>
          ) : (
            <>
              You&apos;ll stop receiving type-level personal alerts for every
              resource type. You may still get events through Notification
              Groups you&apos;re a member of.
            </>
          )}
        </p>
      </GenericModal>
    </div>
  );
};

export default MyNotificationsTab;
